import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { getSalesObjectives, discoverSegmentTargets, importFromUrl } from '../api'

// Segments éligibles à la découverte IA (briefs métier dans backend)
const DISCOVERABLE = new Set(['architecte_be', 'telesurveilleur', 'ecole', 'installateur_prestige'])

const COLOR_MAP = {
  green:  { bar: 'bg-green-500',  bg: 'bg-green-900/20',  text: 'text-green-300',  border: 'border-green-700/40' },
  purple: { bar: 'bg-purple-500', bg: 'bg-purple-900/20', text: 'text-purple-300', border: 'border-purple-700/40' },
  blue:   { bar: 'bg-blue-500',   bg: 'bg-blue-900/20',   text: 'text-blue-300',   border: 'border-blue-700/40' },
  cyan:   { bar: 'bg-cyan-500',   bg: 'bg-cyan-900/20',   text: 'text-cyan-300',   border: 'border-cyan-700/40' },
  yellow: { bar: 'bg-yellow-500', bg: 'bg-yellow-900/20', text: 'text-yellow-300', border: 'border-yellow-700/40' },
  orange: { bar: 'bg-orange-500', bg: 'bg-orange-900/20', text: 'text-orange-300', border: 'border-orange-700/40' },
  gray:   { bar: 'bg-dark-500',   bg: 'bg-dark-900/40',   text: 'text-dark-300',   border: 'border-dark-700' },
}

const STATUS_LABEL = { done: 'Atteint', in_progress: 'En cours', behind: 'En retard' }

export default function SalesObjectivesWidget() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  // Découverte Gemini : modal ouverte sur un segment
  const [discoverModal, setDiscoverModal] = useState(null) // { segment, label, icon }
  const [discoverLoading, setDiscoverLoading] = useState(false)
  const [discoverResults, setDiscoverResults] = useState(null)
  const [importingSiren, setImportingSiren] = useState(null) // SIREN/nom en cours d'import
  const navigate = useNavigate()

  const openDiscover = async (segment, label, icon) => {
    setDiscoverModal({ segment, label, icon })
    setDiscoverResults(null)
    setDiscoverLoading(true)
    try {
      const r = await discoverSegmentTargets(segment, 8)
      setDiscoverResults(r.data)
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur Gemini')
      setDiscoverModal(null)
    } finally {
      setDiscoverLoading(false)
    }
  }

  const handleImportSuggestion = async (suggestion) => {
    // Import via SIREN si Gemini en donne un, sinon recherche par nom
    const ref = suggestion.siren_hint?.replace(/\D/g, '') || suggestion.recherche_hint || suggestion.nom
    if (!ref) { toast.error('Référence manquante'); return }
    setImportingSiren(ref)
    try {
      const r = await importFromUrl(ref)
      if (r.data.already_existed) {
        toast(`${r.data.nom} — déjà en base`, { icon: 'ℹ️' })
      } else {
        toast.success(`${r.data.nom} importé`)
      }
      navigate('/crm', { state: { openCompanyId: r.data.id } })
      setDiscoverModal(null)
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible à trouver (SIRENE)')
    } finally {
      setImportingSiren(null)
    }
  }

  useEffect(() => {
    getSalesObjectives()
      .then(r => setData(r.data))
      .catch(() => setData({ objectives: [] }))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return null
  if (!data || !data.objectives || data.objectives.length === 0) return null

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-dark-50 flex items-center gap-2">
          🎯 Objectifs commerciaux
          <span className="text-xs font-normal text-dark-500">brief Nicolas Chevalier</span>
        </h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {data.objectives.map(obj => {
          const c = COLOR_MAP[obj.color] || COLOR_MAP.gray
          return (
            <div key={obj.segment} className={clsx('rounded-xl border p-3', c.bg, c.border)}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className={clsx('font-medium text-sm', c.text)}>
                  {obj.icon} {obj.label}
                </span>
                <span className="text-xs">{obj.status_icon}</span>
              </div>
              <p className="text-xs text-dark-500 mb-2">
                {obj.spec} · {obj.period_label}
              </p>
              <div className="flex items-baseline gap-2 mb-2">
                <span className={clsx('text-2xl font-bold', c.text)}>{obj.done}</span>
                <span className="text-dark-500 text-sm">/ {obj.target}</span>
                <span className="text-dark-500 text-xs ml-auto">
                  {STATUS_LABEL[obj.status] || obj.status}
                </span>
              </div>
              <div className="h-1.5 bg-dark-900/50 rounded-full overflow-hidden mb-3">
                <div className={clsx('h-full transition-all', c.bar)}
                  style={{ width: `${obj.progress_pct}%` }} />
              </div>
              {obj.remaining > 0 && obj.suggestions.length > 0 && (
                <div>
                  <p className="text-xs text-dark-400 mb-1.5">
                    💡 Dans ta base ({obj.remaining} restant{obj.remaining > 1 ? 's' : ''}) :
                  </p>
                  <div className="space-y-1">
                    {obj.suggestions.slice(0, 3).map(s => (
                      <button
                        key={s.id}
                        onClick={() => navigate('/crm', { state: { openCompanyId: s.id } })}
                        className="w-full text-left text-xs bg-dark-900/40 hover:bg-dark-900/70 rounded-md px-2 py-1.5 transition-colors flex items-center justify-between gap-2"
                      >
                        <span className="text-dark-100 truncate">{s.nom}</span>
                        <span className="text-dark-500 text-[10px] whitespace-nowrap">
                          {s.ville || '—'}
                          {s.ai_score != null && ` · ${s.ai_score}`}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {obj.remaining === 0 && (
                <p className="text-xs text-green-400 mt-1">✅ Objectif atteint pour {obj.period_label.toLowerCase()}</p>
              )}
              {/* Bouton découverte IA — suggestions métier réelles */}
              {DISCOVERABLE.has(obj.segment) && (
                <button
                  onClick={() => openDiscover(obj.segment, obj.label, obj.icon)}
                  className="mt-3 w-full text-xs bg-purple-900/30 hover:bg-purple-900/50 border border-purple-700/40 text-purple-200 rounded-md py-1.5 flex items-center justify-center gap-1.5 transition-colors"
                >
                  ✨ Découvrir des cibles (IA)
                </button>
              )}
            </div>
          )
        })}
      </div>

      {/* Modale de découverte IA */}
      {discoverModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
             onClick={() => setDiscoverModal(null)}>
          <div className="bg-dark-800 border border-purple-700/50 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl"
               onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-dark-800 px-5 py-4 border-b border-dark-700 flex items-center justify-between">
              <div>
                <h3 className="text-dark-50 font-semibold flex items-center gap-2">
                  ✨ Découverte IA — {discoverModal.icon} {discoverModal.label}
                </h3>
                <p className="text-dark-500 text-xs mt-0.5">
                  Cibles réelles dans ton secteur, proposées par Gemini
                </p>
              </div>
              <button onClick={() => setDiscoverModal(null)} className="text-dark-400 hover:text-dark-50 text-xl">✕</button>
            </div>
            <div className="p-5">
              {discoverLoading && (
                <p className="text-dark-300 text-sm text-center py-8">⏳ Recherche en cours…</p>
              )}
              {discoverResults && discoverResults.results && discoverResults.results.length === 0 && (
                <p className="text-dark-400 text-sm text-center py-6">Aucune cible trouvée. Réessaye.</p>
              )}
              {discoverResults && discoverResults.results && discoverResults.results.length > 0 && (
                <>
                  <p className="text-dark-400 text-xs mb-3">
                    📍 Départements ciblés : <span className="text-dark-200">{discoverResults.depts_used}</span> · {discoverResults.results.length} suggestions
                  </p>
                  <div className="space-y-2">
                    {discoverResults.results.map((s, i) => (
                      <div key={i} className="bg-dark-900/50 border border-dark-700 rounded-lg p-3">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex-1 min-w-0">
                            <p className="text-dark-50 font-medium text-sm">{s.nom}</p>
                            <p className="text-dark-500 text-xs mt-0.5">
                              📍 {s.ville || '?'}{s.dept ? ` (${s.dept})` : ''}
                              {s.siren_hint && ` · SIREN ${s.siren_hint}`}
                            </p>
                          </div>
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <button
                              onClick={() => handleImportSuggestion(s)}
                              disabled={importingSiren === (s.siren_hint || s.nom)}
                              className="text-xs px-2.5 py-1 rounded bg-primary-600/30 border border-primary-600/50 text-primary-200 hover:bg-primary-600/50 disabled:opacity-50"
                            >
                              {importingSiren === (s.siren_hint || s.nom) ? '⏳' : '➕ Importer'}
                            </button>
                            <a
                              href={`https://www.google.com/search?q=${encodeURIComponent(s.nom + ' ' + (s.ville || ''))}`}
                              target="_blank" rel="noreferrer"
                              className="text-[10px] text-center text-dark-400 hover:text-dark-200"
                              title="Vérifier sur Google"
                            >
                              🔍 Google
                            </a>
                          </div>
                        </div>
                        {s.raison && (
                          <p className="text-dark-300 text-xs italic mt-1.5">{s.raison}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-dark-500 text-[11px] mt-4">
                    💡 "Importer" cherche l'entreprise sur recherche-entreprises.api.gouv.fr (gratuit) et l'ajoute au CRM. Si le nom est imprécis, l'import peut échouer — utilise alors 🔍 Google pour vérifier puis cherche par SIREN.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
